// src/components/tutor/TutorDashboard.js
import React, { useState, useEffect, useMemo, useCallback, useRef, Component } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import {
  doc, getDoc,
  collection, query, where, orderBy, limit, onSnapshot,
} from "firebase/firestore";

// --- REACT ICONS IMPORTS ---
import { TfiAnnouncement } from "react-icons/tfi";
import { FaRegCalendar } from "react-icons/fa6";
import { PiStudent, PiBooks } from "react-icons/pi";
import { LuCalendarClock, LuLogOut, LuChevronLeft, LuChevronRight, LuBell, LuLightbulb } from "react-icons/lu";
import { CiMoneyCheck1, } from "react-icons/ci";
import { BsPerson } from "react-icons/bs";
import { FiFileText } from "react-icons/fi";

// --- IMPORTED COMPONENTS ---
import TutorMyInfo from "./myinfo/TutorMyInfo";
import TutorSubjectMatrix from "./myinfo/TutorSubjectMatrix";
import TutorAvailability from "./availability/TutorAvailability";
import TutorAnnouncementsPage from "./tutor_announcements/TutorAnnouncements";
import SuggestionBox from "../suggestions/SuggestionBox";
import useSuggestionBadge from "../suggestions/useSuggestionBadge";
import TutorSchedulePage from "./TutorSchedulePage";
import TutorPayroll from "./tutor_payroll/TutorPayroll";
import TutorStudentManagement from "./student/directory/TutorStudentManagement";
import DiagnosticReports from "../admin/diagnostics/DiagnosticReports";
import StudentPlansPage from "../admin/plans/StudentPlansPage";
import { buildDiagnosticReportUrl } from "../admin/diagnostics/diagnosticReportsData";
import CurriculumApp from "../admin/curriculum/CurriculumApp";
import SessionLogsWorkspace from "../session_logs/SessionLogsWorkspace";

// Mobile Components
import MobileBottomNav from "./mobile/MobileBottomNav";
import MobileTutorSchedule from "./mobile/MobileTutorSchedule";
import MobileTutorAvailability from "./mobile/MobileTutorAvailability";
import MobileTutorAnnouncements from "./mobile/MobileTutorAnnouncements";
import MobileTutorNotifications from "./mobile/MobileTutorNotifications";
import MobileTutorProfile from "./mobile/MobileTutorProfile";
import "./mobile/MobileTutor.css";

// PWA badge utility
import { updateAppBadge } from "../../utils/appBadge";

// --- ROUTING ---
import {
  TUTOR_DEFAULT_TAB,
  getTutorPathForTab,
  getTutorTabFromPath,
  getCanonicalTutorPath,
  getGroupKeyForTutorTab,
  shouldPreserveTutorSearch,
} from "./tutorPortalRoutes";

// --- STYLES ---
import "./TutorDashboard.css";

// ── Error Boundary ─────────────────────────────────────────────────────────────
// Wraps each desktop tab so a crash in one tab (e.g. Announcements) cannot
// take down the entire dashboard and leave tutors unable to navigate.
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("TutorDashboard tab error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, color: "#71717a" }}>
          <strong style={{ color: "#09090b" }}>Something went wrong in this tab.</strong>
          <p style={{ marginTop: 8, fontSize: 13 }}>
            Please refresh the page or switch to another tab. If this keeps happening,
            contact an administrator.
          </p>
          <pre style={{ marginTop: 12, fontSize: 11, color: "#a1a1aa", whiteSpace: "pre-wrap" }}>
            {this.state.error?.message}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
// ──────────────────────────────────────────────────────────────────────────────

/* -----------------------------
   Icons
-------------------------------- */
function Icon({ name, active }) {
  // Determine color based on active state
  const color = active ? "#2D68FE" : "#64748b";
  const size = 18; 

  switch (name) {
    case "speaker": // Announcements
      return <TfiAnnouncement color={color} size={size} />;
    case "idea": // Suggestions
      return <LuLightbulb color={color} size={size} />;
    case "calendar": // Schedule
      return <FaRegCalendar color={color} size={size} />;
    case "students": // Students
      return <PiStudent color={color} size={size} />;
    case "book": // Curriculum
      return <PiBooks color={color} size={size} />;
    case "clock": // Availability
      return <LuCalendarClock color={color} size={size} />;
    case "dollar": // Payroll
      return <CiMoneyCheck1 color={color} size={20} />; // Bumped size slightly for visibility
    case "session": // Session
      return <FiFileText color={color} size={18} />;
    case "user": // My Info
      return <BsPerson color={color} size={size} />;
    case "logout": // Logout
      return <LuLogOut color={color} size={size} />;
    case "chev": // Collapse Sidebar
      return <LuChevronLeft color={color} size={size} />;
    case "chevRight": // Expand Sidebar / Nav Arrow
      return <LuChevronRight color={color} size={size} />;
    default:
      return null;
  }
}

export default function TutorDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  // Active desktop tab is derived from the URL so browser back/forward and
  // direct links stay in sync. (Mobile uses its own `mobileTab` state below.)
  const activeTab = useMemo(
    () => getTutorTabFromPath(location.pathname) || TUTOR_DEFAULT_TAB,
    [location.pathname]
  );

  const setActiveTab = useCallback(
    (tabKey) => {
      const nextPath = getTutorPathForTab(tabKey);
      if (nextPath) navigate(nextPath);
    },
    [navigate]
  );

  const [openGroups, setOpenGroups] = useState({
    student: false,
    session: false,
    myinfo: false,
  });

  const [userProfile, setUserProfile] = useState({ name: "", email: "" });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setCurrentUserId(currentUser.uid);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userDocRef);

          if (userSnap.exists()) {
            const data = userSnap.data();
            setUserProfile({
              name: data.name || currentUser.displayName || "Tutor",
              email: data.email || currentUser.email
            });
          } else {
            setUserProfile({
              name: currentUser.displayName || "Tutor",
              email: currentUser.email
            });
          }
        } catch (err) {
          console.error("Error fetching user profile:", err);
        }
      } else {
        setCurrentUserId(null);
      }
    });
    return () => {
      window.removeEventListener("resize", handleResize);
      unsubscribe();
    };
  }, []);

  const userInitial = useMemo(() => (userProfile.name?.trim()?.[0] || "T").toUpperCase(), [userProfile.name]);

  const handleLogout = () => {
    auth.signOut().catch((err) => console.error("Logout Error:", err));
  };

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Keep the sidebar group containing the active tab expanded.
  useEffect(() => {
    const groupKey = getGroupKeyForTutorTab(activeTab);
    if (!groupKey) return;
    setOpenGroups((prev) => (prev[groupKey] ? prev : { ...prev, [groupKey]: true }));
  }, [activeTab]);

  // Normalize the URL to its canonical tutor path (e.g. "/" → "/announcements",
  // bare group paths → a default child) so every page has a stable address.
  // Desktop only — the mobile layout drives itself from `mobileTab`.
  useEffect(() => {
    if (isMobile) return;

    const canonicalPath = getCanonicalTutorPath(location.pathname, activeTab);
    if (!canonicalPath) {
      // Unknown path under the tutor portal — send to the default page.
      if (location.pathname !== getTutorPathForTab(TUTOR_DEFAULT_TAB)) {
        navigate(getTutorPathForTab(TUTOR_DEFAULT_TAB), { replace: true });
      }
      return;
    }

    const preserveSearch =
      canonicalPath === location.pathname && shouldPreserveTutorSearch(location.pathname);
    const nextPath = preserveSearch ? `${canonicalPath}${location.search}` : canonicalPath;

    if (`${location.pathname}${location.search}` !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [isMobile, activeTab, location.pathname, location.search, navigate]);

  const navItems = [
    { key: "announcements", label: "Announcements", icon: "speaker", onClick: () => setActiveTab("announcements") },
    { key: "suggestions", label: "Suggestions", icon: "idea", onClick: () => setActiveTab("suggestions") },
    { key: "schedule", label: "Schedule", icon: "calendar", onClick: () => setActiveTab("schedule") },

    { 
      key: "session", 
      label: "Session", 
      icon: "session",
      isGroup: true,
      children: [
        { key: "session__log", label: "Log", onClick: () => setActiveTab("session__log") },
        { key: "session__progress", label: "Progress Report", onClick: () => setActiveTab("session__progress") },
      ],
    },

    {
      key: "curriculum",
      label: "Curriculum",
      icon: "book",
      onClick: () => setActiveTab("curriculum"),
    },
    
    { 
      key: "student", 
      label: "Students", 
      icon: "students", 
      isGroup: true,
      children: [
        { key: "student__directory", label: "Directory", onClick: () => setActiveTab("student__directory") },
        { key: "student__diagnostics", label: "Diagnostic Reports", onClick: () => setActiveTab("student__diagnostics") },
        { key: "student__plans", label: "Plan", onClick: () => setActiveTab("student__plans") },
      ]
    },

    { key: "availability", label: "Availability", icon: "clock", onClick: () => setActiveTab("availability") },
    { key: "payroll", label: "Payroll", icon: "dollar", onClick: () => setActiveTab("payroll") },

    { 
      key: "myinfo", 
      label: "My Info", 
      icon: "user",
      isGroup: true,
      children: [
        { key: "myinfo__profile", label: "Profile", onClick: () => setActiveTab("myinfo__profile") },
        { key: "myinfo__subjects", label: "Subjects", onClick: () => setActiveTab("myinfo__subjects") }, // <--- ADD THIS LINE
        // ... (payroll, etc)
      ]
    },
  ];

  // --- RENDER CONTENT (Desktop) ---
  const renderDesktopMain = () => {
    // UPDATED: Used overflowY: "auto" for sections that need scrolling
    // Each case is wrapped in TabErrorBoundary so a crash in one tab
    // does not take down the sidebar or the rest of the dashboard.
    switch(activeTab) {
      case "announcements": return <TabErrorBoundary key="announcements"><div style={{ overflow: "hidden", height: "100%" }}><TutorAnnouncementsPage /></div></TabErrorBoundary>;
      case "suggestions": return <TabErrorBoundary key="suggestions"><div style={{ overflow: "hidden", height: "100%" }}><SuggestionBox viewerRole="tutor" /></div></TabErrorBoundary>;
      case "schedule": return <TabErrorBoundary key="schedule"><div style={{ overflow: "auto", height: "100%" }}><TutorSchedulePage /></div></TabErrorBoundary>;
      case "curriculum": return <TabErrorBoundary key="curriculum"><div style={{ overflow: "hidden", height: "100%" }}><CurriculumApp basePath="/curriculum" permissions={{ canDelete: false }} /></div></TabErrorBoundary>;
      case "availability": return <TabErrorBoundary key="availability"><div style={{ overflowY: "auto", height: "100%" }}><TutorAvailability /></div></TabErrorBoundary>;
      case "student__directory": return <TabErrorBoundary key="student__directory"><div style={{ overflowY: "auto", height: "100%" }}><TutorStudentManagement /></div></TabErrorBoundary>;
      case "student__diagnostics": return <TabErrorBoundary key="student__diagnostics"><div style={{ overflow: "auto", height: "100%" }}><DiagnosticReports mode="active" getReportUrl={(a) => buildDiagnosticReportUrl(a, { prefix: "/tutor" })} /></div></TabErrorBoundary>;
      case "student__plans": return <TabErrorBoundary key="student__plans"><div style={{ overflow: "auto", height: "100%" }}><StudentPlansPage /></div></TabErrorBoundary>;
      case "student": return <div style={{ padding: 20 }}>Select a student option.</div>;
      case "payroll": return <TabErrorBoundary key="payroll"><div style={{ overflowY: "auto", height: "100%" }}><TutorPayroll /></div></TabErrorBoundary>;
      case "session__log": return <TabErrorBoundary key="session__log"><div style={{ overflowY: "auto", height: "100%" }}><SessionLogsWorkspace viewerRole="tutor" page="log" /></div></TabErrorBoundary>;
      case "session__progress": return <TabErrorBoundary key="session__progress"><div style={{ overflowY: "auto", height: "100%" }}><SessionLogsWorkspace viewerRole="tutor" page="progress" /></div></TabErrorBoundary>;
      case "session": return <div style={{ padding: 20 }}>Select Log or Progress Report.</div>;
      case "myinfo__profile": return <TabErrorBoundary key="myinfo__profile"><div style={{ overflowY: "auto", height: "100%" }}><TutorMyInfo /></div></TabErrorBoundary>;
      case "myinfo": return <TabErrorBoundary key="myinfo"><div style={{ overflowY: "auto", height: "100%" }}><TutorMyInfo /></div></TabErrorBoundary>;
      case "myinfo__subjects": return <TabErrorBoundary key="myinfo__subjects"><div style={{ overflowY: "auto", height: "100%" }}><TutorSubjectMatrix /></div></TabErrorBoundary>;

      default: return <div style={{ padding: 20 }}>Select a section.</div>;
    }
  };

  // --- Mobile Logic ---
  const [mobileTab,          setMobileTab]          = useState("announcements");
  const [unreadCount,        setUnreadCount]         = useState(0);
  const [notificationsOpen,  setNotificationsOpen]   = useState(false);
  const [scheduleTargetDate, setScheduleTargetDate]  = useState(null);

  // ── Auth UID (needed for the Firestore notification listener) ──────────────
  const [currentUserId, setCurrentUserId] = useState(null);

  // ── In-app notification badge & bell animation ─────────────────────────────
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [bellRinging,      setBellRinging]      = useState(false);
  // Ref so the onSnapshot callback always reads the latest value without
  // being re-subscribed every time the overlay opens/closes.
  const notificationsOpenRef = useRef(false);
  const lastViewedTsRef      = useRef(null);

  // Keep the ref in sync with state
  useEffect(() => { notificationsOpenRef.current = notificationsOpen; }, [notificationsOpen]);

  // Real-time listener — counts notifications newer than "last viewed" timestamp
  useEffect(() => {
    if (!isMobile || !currentUserId) return;

    // Initialise lastViewedTs from localStorage; default to now so old
    // notifications don't flood the badge on first install.
    const stored = localStorage.getItem(`notif_last_viewed_${currentUserId}`);
    lastViewedTsRef.current = stored ? new Date(parseInt(stored, 10)) : new Date();

    const q = query(
      collection(db, "tutor_notifications"),
      where("uid", "==", currentUserId),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const unsub = onSnapshot(q, (snap) => {
      // Don't update badge while the overlay is already visible
      if (notificationsOpenRef.current) return;

      const lastTs = lastViewedTsRef.current;
      let unread = 0;
      snap.docs.forEach((d) => {
        const createdAt = d.data().createdAt?.toDate?.();
        if (createdAt && lastTs && createdAt > lastTs) unread++;
      });

      setNotifUnreadCount((prev) => {
        if (unread > prev) {
          // New notification arrived — ring the bell once
          setBellRinging(true);
          setTimeout(() => setBellRinging(false), 700);
        }
        return unread;
      });
    }, (err) => {
      console.error("tutor_notifications listener error:", err);
    });

    return () => unsub();
  }, [isMobile, currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open overlay + mark all current notifications as "seen"
  const openNotifications = useCallback(() => {
    const now = Date.now();
    if (currentUserId) {
      localStorage.setItem(`notif_last_viewed_${currentUserId}`, String(now));
      lastViewedTsRef.current = new Date(now);
    }
    setNotifUnreadCount(0);
    setNotificationsOpen(true);
  }, [currentUserId]);

  // Close overlay + reset all swipe state
  const closeNotifications = useCallback(() => {
    setNotificationsOpen(false);
  }, []);

  // ── PWA Home Screen badge sync ──────────────────────────────────────────────
  // Mirrors the in-app unread badge onto the installed PWA icon.
  // updateAppBadge is a safe no-op on browsers/platforms that don't support the
  // Badging API (e.g. Firefox, non-installed web views, iOS without permission).
  // Future: replace `unreadCount` with a combined total when message/alert
  // counts are added — updateAppBadge(announcements + messages + alerts).
  useEffect(() => {
    updateAppBadge(unreadCount);
  }, [unreadCount]);
  // ───────────────────────────────────────────────────────────────────────────

  // Holds an announcementId that arrived via notification click (Cache API or postMessage)
  const [pendingAnnouncementId, setPendingAnnouncementId] = useState(null);

  // On mount: check Cache API for a pending notification intent.
  // Handles both announcement deep-links (cold start) and session alerts.
  useEffect(() => {
    async function checkNotificationIntent() {
      try {
        if (!window.caches) return;
        const cache = await caches.open('notification-intents-v1');
        const response = await cache.match('/notification-intent');
        if (!response) return;
        const data = await response.json();
        await cache.delete('/notification-intent');
        if (Date.now() - data.ts > 60_000) return; // stale — ignore

        if (data.announcementId) {
          setMobileTab('announcements');
          setPendingAnnouncementId(data.announcementId);
        } else if (data.type === "session") {
          openNotifications();
        }
      } catch (e) {
        // Non-fatal
      }
    }
    checkNotificationIntent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for postMessage from the service worker.
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const handler = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK" && event.data.announcementId) {
        setMobileTab("announcements");
        setPendingAnnouncementId(event.data.announcementId);
      }
      if (event.data?.type === "SESSION_NOTIFICATION_CLICK") {
        openNotifications();
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Called when a notification entry is tapped — navigate to Schedule at targetDate.
  const handleNotificationDeepLink = (targetDate) => {
    setNotificationsOpen(false);
    setMobileTab("schedule");
    setScheduleTargetDate(targetDate || null);
  };

  const handleMobileTabClick = (tKey) => {
    setMobileTab(tKey);
  };

  // --- Suggestions nav badge ---
  // Red count = suggestions of yours completed since you last opened the tab;
  // grey count = read but not completed (completed overrides read). Opening the
  // Suggestions tab on either layout marks everything seen.
  const {
    count: suggestionsBadgeCount,
    tone: suggestionsBadgeTone,
    markSeen: markSuggestionsSeen,
  } = useSuggestionBadge({ uid: currentUserId });

  useEffect(() => {
    if (activeTab === "suggestions" || mobileTab === "suggestions") markSuggestionsSeen();
  }, [activeTab, mobileTab, markSuggestionsSeen]);

  const renderMobileContent = () => {
    switch(mobileTab) {
      case "announcements": return (
        <MobileTutorAnnouncements
          pendingAnnouncementId={pendingAnnouncementId}
          onAnnouncementOpened={() => setPendingAnnouncementId(null)}
          onUnreadCountChange={setUnreadCount}
        />
      );
      case "suggestions": return <SuggestionBox viewerRole="tutor" />;
      case "availability": return <MobileTutorAvailability />;
      case "schedule":     return (
        <MobileTutorSchedule
          currentUserUid={auth.currentUser?.uid}
          targetDate={scheduleTargetDate}
        />
      );
      case "profile":      return <MobileTutorProfile />;
      default: return <div>Coming Soon</div>;
    }
  };

  if (isMobile) {
    return (
      <div className="mobile-layout">
        {renderMobileContent()}
        <MobileBottomNav
          activeTab={mobileTab}
          onTabChange={handleMobileTabClick}
          unreadCount={unreadCount}
          suggestionCount={suggestionsBadgeCount}
          suggestionTone={suggestionsBadgeTone}
        />

        {/* Floating bell — opens notification history overlay */}
        <button
          className={`mobile-notif-fab${bellRinging ? " mobile-notif-fab--ringing" : ""}`}
          onClick={openNotifications}
          aria-label={`Open notifications${notifUnreadCount > 0 ? `, ${notifUnreadCount} unread` : ""}`}
        >
          <span className="mobile-notif-bell-icon">
            <LuBell size={19} />
          </span>
          {notifUnreadCount > 0 && (
            <span className="mobile-notif-fab-badge">
              {notifUnreadCount > 9 ? "9+" : notifUnreadCount}
            </span>
          )}
        </button>

        {/* Notifications history overlay */}
        {notificationsOpen && (
          <div
            className="mobile-notif-overlay"
          >
            <MobileTutorNotifications
              onClose={closeNotifications}
              onNavigate={handleNotificationDeepLink}
            />
          </div>
        )}
      </div>
    );
  }

  // --- DESKTOP LAYOUT ---
  return (
    <div className="td-shell">
      {/* SIDEBAR */}
      <aside className={`td-sidebar ${isSidebarExpanded ? "td-sidebar--expanded" : "td-sidebar--collapsed"}`}>
        <div className="td-sidebarHeader">
          {isSidebarExpanded ? (
            <>
              <div className="td-brandRow">
                <div className="ad-brandLogo" aria-hidden="true">
                  <img src="/images/logo.png" alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                {/* Updated Brand Column */}
                <div className="td-brandCol">
                  <div className="td-brandName">True Education</div>
                  <div className="td-brandSubtitle">Tutor Portal</div>
                </div>
              </div>
              <button
                className="td-collapseBtn"
                onClick={() => setIsSidebarExpanded(false)}
                aria-label="Collapse sidebar"
              >
                <Icon name="chev" active={false} />
              </button>
            </>
          ) : (
            <button
              className="td-collapseBtn"
              onClick={() => setIsSidebarExpanded(true)}
              aria-label="Expand sidebar"
              style={{ margin: "0 auto" }}
            >
              <Icon name="chevRight" active={false} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="td-nav">
          {navItems.map((item) => {
            const isGroup = !!item.isGroup;
            const isActive = item.key === activeTab;
            
            if (isGroup) {
              const isOpen = !!openGroups[item.key];
              const activeChild = item.children?.some((c) => c.key === activeTab);

              return (
                <div className="td-navGroup" key={item.key}>
                  <div
                    className={`td-navItem ${activeChild ? "td-navItem--active" : ""}`}
                    data-tooltip={item.label}
                    onClick={() => toggleGroup(item.key)}
                  >
                    <div className="td-navIcon">
                      <Icon name={item.icon} active={activeChild} />
                    </div>
                    {isSidebarExpanded && <div className="td-navLabel">{item.label}</div>}
                    <div className="td-navRight">
                      {isSidebarExpanded && (
                        <span className="td-navChevron" style={{transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}}>
                          <Icon name="chevRight" active={activeChild} />
                        </span>
                      )}
                    </div>
                    {!isSidebarExpanded && <span className="td-tooltip">{item.label}</span>}
                  </div>

                  {/* Nested Sub-items */}
                  {isOpen && isSidebarExpanded && (
                    <div className="td-subNav">
                      {item.children.map((child) => {
                        const childActive = child.key === activeTab;
                        return (
                          <div
                            key={child.key}
                            className={`td-subNavItem ${childActive ? "td-subNavItem--active" : ""}`}
                            onClick={() => child.onClick()}
                          >
                            <span className="td-subNavDot" />
                            <span className="td-subNavLabel">{child.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div
                key={item.key}
                className={`td-navItem ${isActive ? "td-navItem--active" : ""}`}
                data-tooltip={item.label}
                onClick={() => item.onClick()}
              >
                <div className="td-navIcon">
                  <Icon name={item.icon} active={isActive} />
                </div>
                {isSidebarExpanded && <div className="td-navLabel">{item.label}</div>}
                <div className="td-navRight">
                  {item.isExternal && isSidebarExpanded && <span className="td-extMark">↗</span>}
                  {item.key === "suggestions" && suggestionsBadgeCount > 0 && isSidebarExpanded && (
                    <span className={`td-badge ${suggestionsBadgeTone === "muted" ? "td-badge--muted" : ""}`}>
                      {suggestionsBadgeCount > 99 ? "99+" : suggestionsBadgeCount}
                    </span>
                  )}
                </div>
                {item.key === "suggestions" && suggestionsBadgeCount > 0 && !isSidebarExpanded && (
                  <span
                    className={`td-navDot ${suggestionsBadgeTone === "muted" ? "td-navDot--muted" : ""}`}
                    aria-hidden="true"
                  />
                )}
                {!isSidebarExpanded && <span className="td-tooltip">{item.label}</span>}
              </div>
            );
          })}
        </nav>

        <div className="td-spacer" />

        <div className="td-footerNav">
          <div className="td-navItem" onClick={handleLogout} data-tooltip="Logout">
            <div className="td-navIcon"><Icon name="logout" active={false} /></div>
            {isSidebarExpanded && <div className="td-navLabel">Logout</div>}
            {!isSidebarExpanded && <span className="td-tooltip">Logout</span>}
          </div>
        </div>

        {/* User Profile */}
        <div className="td-userCard">
          <div className="td-avatar">{userInitial}</div>
          {isSidebarExpanded && (
            <div className="td-userInfo">
              <div className="td-userName">{userProfile.name}</div>
              <div className="td-userEmail">{userProfile.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="td-main">
        <div className="td-mainInner">
          <div className="td-panel">{renderDesktopMain()}</div>
        </div>
      </div>
    </div>
  );
}
